import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, FileText, Clock, Home, CheckCircle2, AlertCircle, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function ProcessPage() {
  const steps = [
    {
      number: "01",
      title: "Sell Your Investment Property",
      description: "The process begins when a property owner sells a qualifying property. Before closing, you must engage a qualified intermediary (QI) to facilitate the 1031 exchange. The QI holds the proceeds from the sale — you cannot take possession of the funds directly.",
      icon: Home,
      tips: [
        "Engage a qualified intermediary before closing",
        "The QI will prepare exchange documents",
        "Sale proceeds go directly to the QI",
        "You cannot touch the funds"
      ]
    },
    {
      number: "02",
      title: "Identify Replacement Properties",
      description: "Within 45 days of the sale, you must identify potential replacement properties in writing to your qualified intermediary. Most buyers use the '3-Property Rule' which allows identification of up to 3 properties without regard to value.",
      icon: FileText,
      tips: [
        "45-day deadline is strict — no extensions",
        "Identification must be in writing to your QI",
        "Identification must be unambiguous — include the property address or legal description, and for condos, the specific unit number is required",
        "You can revoke, replace, or amend your identification at any time within the 45-day window — but once the window closes, no changes are permitted",
        "3-Property Rule: identify up to 3 properties of any value",
        "200% Rule: identify more than 3 properties, provided their combined fair market value does not exceed 200% of the value of the relinquished property",
        "95% Rule: if more than 3 properties are identified and the 200% Rule is exceeded, the buyer must acquire at least 95% of the total value of all identified properties",
        "These alternative rules allow buyers to identify multiple condo units — for example, 4 or more sponsor units in the same building — as long as the total value stays within the 200% threshold"
      ]
    },
    {
      number: "03",
      title: "Complete the Exchange",
      description: "You must acquire the replacement property within 180 days of selling your original property (or by your tax return due date, whichever comes first). The QI transfers funds directly to complete the purchase.",
      icon: Clock,
      tips: [
        "180-day deadline includes weekends and holidays",
        "Must acquire at least one identified property",
        "QI transfers funds directly to closing",
        "Title should be taken in the same name as the relinquished property"
      ]
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            className="max-w-3xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="overline mb-4 block">How It Works</span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-[#1A1A2E] leading-[1.1] mb-6" data-testid="process-title">
              The 1031 Exchange Process
            </h1>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed">
              A step-by-step guide to completing a successful 1031 exchange and deferring capital gains taxes on your investment property sale.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* Key Deadlines Banner */}
          <motion.div 
            className="mb-16 p-8 bg-[#EFF6FF] border border-[#0066FF]/20"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-start gap-4">
              <AlertCircle className="w-6 h-6 text-[#0066FF] flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-medium text-[#1A1A2E] mb-2">Critical Deadlines</h3>
                <p className="text-slate-600">
                  The IRS strictly enforces 1031 exchange deadlines. Missing either the 45-day identification period or 180-day exchange period will disqualify the exchange, resulting in immediate capital gains tax liability.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Steps */}
          <div className="space-y-16">
            {steps.map((step, idx) => (
              <motion.div
                key={idx}
                className="grid lg:grid-cols-12 gap-8 items-start"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <div className="lg:col-span-4">
                  <div className="flex items-center gap-4 mb-4">
                    <span className="text-6xl font-bold text-slate-200">{step.number}</span>
                    <step.icon className="w-8 h-8 text-[#0066FF]" strokeWidth={1.5} />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-light text-[#1A1A2E] leading-tight">
                    {step.title}
                  </h2>
                </div>
                
                <div className="lg:col-span-8">
                  <p className="text-lg text-slate-600 leading-relaxed mb-6">
                    {step.description}
                  </p>
                  <div className="bg-slate-50 p-6 border border-slate-100">
                    <h4 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-4">Key Points</h4>
                    <ul className="space-y-3">
                      {step.tips.map((tip, tipIdx) => (
                        <li key={tipIdx} className="flex items-start gap-3 text-slate-600">
                          <CheckCircle2 className="w-5 h-5 text-[#0066FF] flex-shrink-0 mt-0.5" />
                          <span>{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Sponsor Unit Strategy */}
          <motion.div
            className="mt-16 p-8 bg-gradient-to-br from-[#0066FF]/5 to-blue-50 border border-[#0066FF]/15 rounded-xl"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            data-testid="sponsor-unit-strategy"
          >
            <div className="grid lg:grid-cols-12 gap-8 items-start">
              <div className="lg:col-span-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-[#0066FF]/10 rounded-lg flex items-center justify-center">
                    <Shield className="w-5 h-5 text-[#0066FF]" />
                  </div>
                  <span className="text-sm font-semibold text-[#0066FF] uppercase tracking-wider">Safeguard Strategy</span>
                </div>
                <h3 className="text-2xl font-light text-[#1A1A2E] leading-tight">
                  Why Multiple Sponsor Units Protect Your Exchange
                </h3>
              </div>
              <div className="lg:col-span-8">
                <p className="text-lg text-slate-600 leading-relaxed mb-6">
                  One of the greatest risks in a 1031 exchange is losing your identified replacement property before the exchange closes. If a property you've identified sells to another buyer, goes off-market, or falls through during the 45-day identification window, your entire exchange could be jeopardized — resulting in immediate capital gains tax liability.
                </p>
                <p className="text-lg text-slate-600 leading-relaxed mb-6">
                  <span className="font-semibold text-[#1A1A2E]">New development sponsor units solve this problem.</span> Sponsor units are developer-held inventory that remains available and is not subject to the same competitive bidding pressure as resale listings. By identifying multiple sponsor units in the same building and price range, buyers create built-in redundancy: if one unit becomes unavailable, another equivalent unit is ready to step in as your replacement property.
                </p>
                <div className="bg-white p-6 rounded-lg border border-slate-100">
                  <h4 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-4">How This Works</h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3 text-slate-600">
                      <CheckCircle2 className="w-5 h-5 text-[#0066FF] flex-shrink-0 mt-0.5" />
                      <span>Under the <span className="font-semibold">3-Property Rule</span>, you can identify up to 3 replacement properties — use this to designate multiple sponsor units in similar price ranges</span>
                    </li>
                    <li className="flex items-start gap-3 text-slate-600">
                      <CheckCircle2 className="w-5 h-5 text-[#0066FF] flex-shrink-0 mt-0.5" />
                      <span>Sponsor units are typically held by the developer and available on a longer timeline, reducing the risk of losing the property to another buyer</span>
                    </li>
                    <li className="flex items-start gap-3 text-slate-600">
                      <CheckCircle2 className="w-5 h-5 text-[#0066FF] flex-shrink-0 mt-0.5" />
                      <span>Multiple units in the same building at similar price points serve as <span className="font-semibold">placeholder properties</span> — if one falls through, another is already identified and ready to close</span>
                    </li>
                    <li className="flex items-start gap-3 text-slate-600">
                      <CheckCircle2 className="w-5 h-5 text-[#0066FF] flex-shrink-0 mt-0.5" />
                      <span>This approach eliminates the single point of failure that makes traditional 1031 exchanges risky and gives buyers confidence that their tax deferral will be preserved</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Visual Timeline */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-light text-[#1A1A2E] mb-4">
              Exchange Timeline
            </h2>
            <p className="text-lg text-slate-600">
              Key milestones from sale to completion
            </p>
          </motion.div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="hidden md:block absolute top-1/2 left-0 right-0 h-0.5 bg-slate-200 -translate-y-1/2" />
            
            <div className="grid md:grid-cols-4 gap-8">
              {[
                { day: "Day 0", event: "Sale Closes", desc: "QI receives proceeds" },
                { day: "Day 45", event: "ID Deadline", desc: "Properties must be identified" },
                { day: "Day 90", event: "Midpoint", desc: "Target acquisition date" },
                { day: "Day 180", event: "Exchange Deadline", desc: "Purchase must close" },
              ].map((milestone, idx) => (
                <motion.div
                  key={idx}
                  className="relative text-center"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <div className="hidden md:block absolute top-1/2 left-1/2 w-4 h-4 bg-[#0066FF] rounded-full -translate-x-1/2 -translate-y-1/2 z-10" />
                  <div className="bg-white p-6 border border-slate-100 md:mt-12">
                    <span className="text-2xl font-bold text-[#0066FF]">{milestone.day}</span>
                    <h3 className="text-lg font-medium text-[#1A1A2E] mt-2">{milestone.event}</h3>
                    <p className="text-sm text-slate-600 mt-1">{milestone.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How We Help */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="overline mb-4 block">How We Help</span>
              <h2 className="text-2xl md:text-3xl font-light tracking-tight text-[#1A1A2E] leading-tight mb-6">
                Your Buyer's Agent
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                Krieger Real Estate LLC represents you as the buyer. We act as your buyer's agent and advocate, presenting offers and negotiating pricing, terms, and concessions with the sponsor, developer, or bulk condo unit owner.
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  "Review available property options in NYC and Miami",
                  "Review building and ownership considerations",
                  "Coordinate showings and due diligence",
                  "Assist with acquisition and closing process",
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-slate-600">
                    <CheckCircle2 className="w-5 h-5 text-[#0066FF] flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/contact">
                <Button className="btn-primary px-8 py-4 rounded-sm">
                  Get Started
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src="https://images.unsplash.com/photo-1542744095-fcf48d80b0fd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMHBlb3BsZSUyMG1lZXRpbmclMjBwcm9mZXNzaW9uYWwlMjBhcmNoaXRlY3QlMjBvZmZpY2UlMjBkaXZlcnNlfGVufDB8fHx8MTc3MzAyMjA5N3ww&ixlib=rb-4.1.0&q=85"
                alt="Professional consultation"
                className="w-full aspect-[4/3] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#1A1A2E]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-light text-white mb-6">
              Ready to Start Your 1031 Exchange?
            </h2>
            <p className="text-lg text-slate-400 mb-8 max-w-2xl mx-auto">
              Register to explore potential replacement properties and connect with our team of 1031 exchange property sourcing specialists.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/properties">
                <Button className="bg-white text-[#1A1A2E] hover:bg-slate-100 px-8 py-6 text-base rounded-sm">
                  View Properties
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-base rounded-sm">
                  Contact Us
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
